<script setup lang="ts">
import { computed, inject, onBeforeUnmount, ref, watch } from 'vue';
import { NxBanner, NxButton, i18nInjectionKey } from '@blpde/nx-ui';
import { driveFolderSetting, readDriveFolderSetting, sameDriveFolder } from '../config/driveFolder';
import type { DriveFolderPermission, DriveFolderSelection } from '../drive/types';
import { useDrive } from '../drive/useDrive';

// Data binding only: the shared Drive picker and kit controls own their UI.
const props = defineProps<{
  value: unknown;
  editable: boolean;
  permission: DriveFolderPermission;
  controlId?: string;
  height: 'xs' | 's';
  /** A Drive context menu can offer the folder the user already selected. */
  folder?: DriveFolderSelection;
}>();
const emit = defineEmits<{ change: [value: unknown] }>();
const drive = useDrive();
const i18n = inject(i18nInjectionKey, null);
const t = (key: string) => i18n?.t(`settingsList.folder.${key}`) ?? key;
const reference = computed(() => readDriveFolderSetting(props.value));
const resolved = ref<DriveFolderSelection | null>(null);
const failed = ref(false);
const busy = ref(false);
let request = 0;
let mounted = true;
onBeforeUnmount(() => { mounted = false; request += 1; });

watch([reference, () => props.permission], async ([folder]) => {
  const current = ++request;
  resolved.value = null;
  failed.value = false;
  if (!folder) {
    failed.value = props.value !== null && props.value !== undefined;
    return;
  }
  try {
    if (!drive) throw new Error('Drive unavailable');
    const value = await drive.resolveFolder(folder, props.permission);
    if (current === request) resolved.value = value;
  } catch {
    if (current === request) failed.value = true;
  }
}, { immediate: true });

const assigned = computed(() => sameDriveFolder(reference.value, props.folder));
const caption = computed(() => {
  if (props.folder) return t(assigned.value ? 'assigned' : 'useSelected');
  if (failed.value) return t('unavailable');
  if (resolved.value) return `${resolved.value.mountName} / ${resolved.value.path || resolved.value.name}`;
  return t(reference.value ? 'loading' : 'choose');
});
const path = computed(() => resolved.value ? `${resolved.value.mountName} / ${resolved.value.path}` : undefined);

async function choose(): Promise<void> {
  if (!props.editable || busy.value || !drive) return;
  busy.value = true;
  const original = props.value;
  try {
    let selected = props.folder
      ? await drive.resolveFolder(props.folder, props.permission)
      : await drive.pickFolder({ startFolder: reference.value ?? undefined, permission: props.permission });
    if (selected && !selected.companyId) selected = await drive.resolveFolder(selected, props.permission);
    // A scope switch unmounts this binding; locks may also arrive during picking.
    if (mounted && props.editable && props.value === original && selected) {
      emit('change', driveFolderSetting(selected, props.permission));
      failed.value = false;
    }
  } catch {
    if (mounted) failed.value = true;
  } finally {
    if (mounted) busy.value = false;
  }
}
</script>

<template>
  <div>
    <NxButton
      :id="controlId"
      :height="height"
      :disabled="!editable || !drive || busy || assigned"
      :title="path"
      variant="outline"
      @click="choose"
    >
      {{ caption }}
    </NxButton>
    <NxBanner v-if="failed" variant="warning" role="status">{{ t('unavailableHint') }}</NxBanner>
  </div>
</template>
